import themes from '@8x8/oxygen-theme'
import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { Flex } from '~/components/common/containers'

import Modal from '~/components/common/modal'
import Spinner from '~/components/spinner'
import { FUNCTIONS } from '~/core/constants'
import { getIsNarrowLayout } from '~/selectors/ui'
import SlideshowControls from './controls'
import SlideshowList from './list'
import SlideshowNavigationControls from './navigation-controls'

const Container = styled(Flex)`
  background-color: ${themes.dark.ui06};
  ${({ isNarrowLayout }) => isNarrowLayout && 'flex-direction: column-reverse;'}
  height: 100%;
`

const SelectedSlideContainer = styled.div`
  position: relative;

  ${({ isNarrowLayout, theme }) => isNarrowLayout
? `
    height: calc(100% - ${theme.sizes.slideshowHorizontalSidebarHeight}px);
    margin: auto 0;
    width: 100%;
  `
: `
    width: calc(100% - ${theme.sizes.slideshowVerticalSidebarWidth}px);
    margin: 0 auto;
    height: 100%;
  `};

  ${({ fullSize }) => fullSize && `
    height: 100%;
    width: 100%;
    margin: 0;
  `}
`

const Slideshow = ({
  autoPlayNext = false,
  canClose = true,
  fetchingInProgress,
  hasMore = false,
  onFetchMore = FUNCTIONS.NOOP,
  onSelect,
  selectedSlide,
  SelectedSlideComponent,
  SlideActions = FUNCTIONS.NULL,
  SlideComponent,
  slideNameMapping = 'slide',
  slideProps,
  slides
}) => {
  const isNarrowLayout = useSelector(getIsNarrowLayout)
  const slidesCount = slides.length

  const selectedSlideProps = {
    [slideNameMapping]: selectedSlide
  }

  const maybePlayNext = () => {
    if (!autoPlayNext) {
      return
    }

    selectNextSlide()
  }

  const selectNextSlide = async () => {
    const index = slides.indexOf(selectedSlide)

    if (index < slides.length - 1) {
      onSelect(slides[index + 1])
    } else if (hasMore) {
      await onFetchMore()
    }
  }

  const selectPrevSlide = () => {
    const index = slides.indexOf(selectedSlide)

    if (index > 0) {
      onSelect(slides[index - 1])
    }
  }

  useEffect(() => {
    if (!selectedSlide) {
      onSelect(slides[0])
    }
  }, [])

  return (
    <Modal isFullScreen noHeader noPadding>
      <SlideshowControls canClose={canClose}>
        <SlideActions {...selectedSlideProps} />
      </SlideshowControls>

      {!slidesCount
        ? (
          <Spinner />
          )
        : (
          <Container
            isNarrowLayout={isNarrowLayout}
          >
            {slidesCount > 1 && (
              <SlideshowList
                fetchingInProgress={fetchingInProgress}
                fetchMore={onFetchMore}
                selectedSlide={selectedSlide}
                SlideComponent={SlideComponent}
                slideNameMapping={slideNameMapping}
                slideProps={slideProps}
                slides={slides}
              />
            )}

            <SelectedSlideContainer isNarrowLayout={isNarrowLayout} fullSize={slidesCount === 1}>
              <SelectedSlideComponent
                {...selectedSlideProps}
                onEnd={maybePlayNext}
              />
              <SlideshowNavigationControls
                hasMore={hasMore}
                selectedSlide={selectedSlide}
                slides={slides}
                onNext={selectNextSlide}
                onPrevious={selectPrevSlide}
              />
            </SelectedSlideContainer>
          </Container>
          )}

    </Modal>
  )
}

export default Slideshow
